import React, { useEffect } from 'react';
import { bool, func, string } from 'prop-types';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  routes, surveyFieldIds, API_DATE_FORMAT
} from '../../../constants';
import { surveyOptionsType, surveyTypeValues } from '../../../constants/types';
import FormNavigation from '../FormNavigation';
import EmailMessageInput from '../../EmailMessageInput';
import { getMessages } from '../../../i18n';

import SurveyScheduling from '../../SurveyScheduling';
import WithValidation from '../../WithValidation';
import { surveySchedulingValidator } from '../../../validators/surveys';
import ReminderScheduling from '../ReminderScheduling';
import { getCompiledPath, getSubRouteForSurveyType } from '../../../util';

const getDefaultContents = (type) => getMessages(type);

const SurveyStartSchedulingSection = ({ showProblems, surveyType, surveyOptions }) => {
  const initialStartDate = surveyOptions.startDate
    ? surveyOptions.startDate : moment().format(API_DATE_FORMAT);
  if (surveyType === surveyTypeValues.COURSE_SURVEY) {
    return (
      <>
        <h2 className="panel__title">4. <Translate id="startSurvey.startTitle" /></h2>

        <SurveyScheduling
          initialStartDate={surveyOptions.initialStartDate}
          showProblems={showProblems}
          showEndDate={false}
          startLabelTextKey="startSurvey.courseSurvey.startDate"
          startHelpTextKey="startSurvey.courseSurvey.startDateHelp"
        />
      </>
    );
  }

  const defaultTitle = surveyOptions.defaultInvitationEmailTitle || getMessages(`defaultSurveyInvitationTitle.${surveyType}`);
  const defaultContent = surveyOptions.defaultInvitationEmailContent || getDefaultContents(`defaultSurveyInvitationContent.${surveyType}`);

  return (
    <>
      <h2 className="panel__title">6. <Translate id="startSurvey.email.invitationTitle" /></h2>

      <SurveyScheduling
        initialStartDate={initialStartDate}
        showProblems={showProblems}
        showEndDate={false}
        startLabelTextKey="startSurvey.startDate"
        startHelpTextKey="startSurvey.startDateHelp"
      />

      <p><Translate id="startSurvey.email.invitationTitleDescription" /></p>
      <EmailMessageInput
        messageTitleName={surveyFieldIds.INVITATION_TITLE}
        messageContentName={surveyFieldIds.INVITATION_CONTENT}
        defaultTitle={defaultTitle}
        defaultContent={defaultContent}
      />
    </>
  );
};

SurveyStartSchedulingSection.propTypes = {
  showProblems: bool.isRequired,
  surveyType: string.isRequired,
  surveyOptions: surveyOptionsType.isRequired
};

const ReminderSchedulingSection = ({
  surveyType, showProblems, hasInvalidField
}) => {
  if (surveyType === surveyTypeValues.COURSE_SURVEY) {
    return null;
  }

  const defaultTitle = getMessages(`defaultSurveyReminderTitle.${surveyType}`);
  const defaultContent = getDefaultContents(`defaultSurveyReminderContent.${surveyType}`);

  return (
    <>
      <h2 className="panel__title">8. <Translate id="surveyReminder.title" /></h2>

      <ReminderScheduling
        showProblems={showProblems && hasInvalidField(surveyFieldIds.REMINDER_SCHEDULES)}
      />

      <EmailMessageInput
        messageTitleName={surveyFieldIds.REMINDER_TITLE}
        messageContentName={surveyFieldIds.REMINDER_CONTENT}
        defaultTitle={defaultTitle}
        defaultContent={defaultContent}
      />
    </>
  );
};

ReminderSchedulingSection.propTypes = {
  showProblems: bool.isRequired,
  hasInvalidField: func.isRequired,
  surveyType: string.isRequired
};

const SurveyEndSchedulingSection = ({ showProblems, surveyOptions, surveyType }) => {
  if (surveyType === surveyTypeValues.COURSE_SURVEY) {
    <>
      <h2 className="panel__title">5. <Translate id="startSurvey.endTitle" /></h2>

      <SurveyScheduling
        initialEndDate={surveyOptions.endDate}
        showProblems={showProblems}
        showStartDate={false}
        endLabelTextKey="startSurvey.courseSurvey.endDate"
        endHelpTextKey="startSurvey.courseSurvey.endDateHelp"
      />
    </>;
  }

  const defaultTitle = getMessages(`defaultSurveyEndedTitle.${surveyType}`);
  const defaultContent = getDefaultContents(`defaultSurveyEndedContent.${surveyType}`);

  return (
    <>
      <h2 className="panel__title">7. <Translate id="startSurvey.email.endingTitle" /></h2>

      <SurveyScheduling
        initialEndDate={surveyOptions.endDate}
        showProblems={showProblems}
        showStartDate={false}
        endLabelTextKey="startSurvey.endDate"
        endHelpTextKey="startSurvey.endDateHelp"
      />

      <p><Translate id="startSurvey.email.endingTitleDescription" /></p>
      <EmailMessageInput
        messageTitleName={surveyFieldIds.ENDING_TITLE}
        messageContentName={surveyFieldIds.ENDING_CONTENT}
        defaultTitle={defaultTitle}
        defaultContent={defaultContent}
      />
    </>
  );
};

SurveyEndSchedulingSection.propTypes = {
  surveyOptions: surveyOptionsType.isRequired,
  showProblems: bool.isRequired,
  surveyType: string.isRequired
};

const Messages = (props) => {
  const {
    showProblems, surveyOptions, hasInvalidField
  } = props;
  const navigate = useNavigate();
  const surveyTypeRoute = getSubRouteForSurveyType(surveyOptions.surveyType);

  useEffect(() => {
    if (!surveyOptions || !surveyOptions.schemaId) {
      navigate(routes.surveys.index, { replace: true });
    }
  }, []);

  const formRoutes = {
    previous: surveyOptions.id
      ? getCompiledPath(surveyTypeRoute.survey.edit.contents.index, { surveyId: surveyOptions.id })
      : surveyTypeRoute.startSurvey.contents.index,
    next: surveyOptions.id
      ? getCompiledPath(surveyTypeRoute.survey.edit.participants, { surveyId: surveyOptions.id })
      : surveyTypeRoute.startSurvey.participants
  };

  return (
    <section>
      <form className="form">

        <SurveyStartSchedulingSection surveyType={surveyOptions.surveyType} surveyOptions={surveyOptions} showProblems={showProblems} />
        <SurveyEndSchedulingSection surveyType={surveyOptions.surveyType} surveyOptions={surveyOptions} showProblems={showProblems} />
        <ReminderSchedulingSection surveyType={surveyOptions.surveyType} hasInvalidField={hasInvalidField} showProblems={showProblems} />

        <FormNavigation
          canProceed={props.isValid}
          previous={formRoutes.previous}
          showProblemsFunc={props.jumpToProblems}
          next={formRoutes.next}
        />
      </form>
    </section>
  );
};

Messages.propTypes = {
  surveyOptions: surveyOptionsType.isRequired,
  showProblems: bool.isRequired,
  jumpToProblems: func.isRequired,
  isValid: bool.isRequired,
  hasInvalidField: func.isRequired
};

const mapStateToProps = ({ survey }) => ({
  surveyOptions: survey.surveyOptions
});

export default connect(mapStateToProps)(WithValidation(Messages, surveySchedulingValidator));
